// src/components/runningaccount/FacturarCuentaAbiertaModal.jsx
/**
 * FacturarCuentaAbiertaModal Component
 * 
 * Allows invoicing selected CARGO items from a client's running account.
 * This modal reuses the same AFIP/ARCA invoice generation logic as InvoicePreviewModal,
 * but with client data preloaded from the Running Account.
 * 
 * Key Features:
 * - Fixed Tipo de Operación: Factura or Proforma only
 * - Client data preloaded from Running Account
 * - CUIT validation determines available invoice types (A/B/X or X only)
 * - Same selectors as InvoicePreviewModal (Tipo Comprobante, Método Pago, Config Pago)
 * - Uses createInvoiceV2 service for AFIP integration
 * - Same response behavior as Invoice Modal
 * 
 * Value States:
 * - PENDIENTE: Values calculated from current prices (in-flux)
 * - FACTURADO: Values locked at invoicing time (immutable)
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Chip,
    CircularProgress,
    RadioGroup,
    FormControlLabel,
    Radio,
    Tooltip,
    Switch,
    TextField
} from '@mui/material';
import {
    Close as CloseIcon,
    Receipt as ReceiptIcon,
    Send as SendIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    RemoveCircle as MinusCircleIcon,
    PersonSearch as PersonSearchIcon
} from '@mui/icons-material';

// Import shared components and services
import SplitPaymentSelector, { getPaymentMethodName } from '../invoices/SplitPaymentSelector';
import { createInvoiceV2 } from '../../services/invoiceService';
import { runningAccountService } from '../../services/runningAccountService';
import { searchClientByCuit, validateCuitWithAfip, checkTerceroExentoSolicitaA } from '../../services/clientService';
import { INVOICE_FEATURES } from '../../config';
import { useAuth } from '../../context/AuthContext';

/**
 * Validate CUIT format and check if it's valid for Factura A/B
 * @param {string} documentoNro - Document number
 * @param {string} condicionIva - Tax condition code
 * @returns {boolean} True if CUIT is valid for A/B invoices
 */
const isCuitValidForInvoice = (documentoNro, condicionIva) => {
    // Must have a document number of at least 10 characters (CUIT format)
    if (!documentoNro || documentoNro.length < 10) {
        return false;
    }
    
    // Clean the CUIT (remove dashes)
    const cleanCuit = documentoNro.replace(/[-\s]/g, '');
    
    // Must be 11 digits
    if (!/^\d{11}$/.test(cleanCuit)) {
        return false;
    }
    
    // Must have a valid tax condition for Factura A
    const validConditions = ['RI', 'M', 'EX'];
    if (condicionIva && validConditions.includes(condicionIva.toUpperCase())) {
        return true;
    }
    
    // If no condition but valid CUIT format, allow B (Consumidor Final)
    return true;
};

/**
 * FacturarCuentaAbiertaModal Component
 */
function FacturarCuentaAbiertaModal({
    open,
    onClose,
    clientData,
    selectedItems,
    onInvoiceGenerated,
    onClearSelection
}) {
    // === STATE ===
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    
    // Invoice configuration state
    const [invoiceType, setInvoiceType] = useState('X'); // Default to X (safest)
    const [paymentMethod, setPaymentMethod] = useState('201'); // Default: Contado
    
    // Split payment state
    const [paymentMode, setPaymentMode] = useState('single'); // 'single' | 'split'
    const [payment1, setPayment1] = useState({ method: '201', amount: 0 }); // Default: Efectivo
    const [payment2, setPayment2] = useState({ method: '206', amount: 0 }); // Default: Transferencia
    
    // Price source per item (use original price vs current price)
    const [priceSourceMap, setPriceSourceMap] = useState({});

    // Facturar a Tercero state
    const [facturarATercero, setFacturarATercero] = useState(false);
    const [terceroData, setTerceroData] = useState(null);
    const [cuitInputTercero, setCuitInputTercero] = useState('');
    const [isSearchingTercero, setIsSearchingTercero] = useState(false);

    // === COMPUTED VALUES ===

    // Effective client: use tercero when toggle is on and a tercero has been selected
    const effectiveClient = useMemo(() => {
        return (facturarATercero && terceroData) ? terceroData : clientData;
    }, [facturarATercero, terceroData, clientData]);
    
    // Determine if CUIT is valid for Factura A/B
    const { user } = useAuth();
    const afipEnabled = !!user?.afipEnabled;

    const cuitValid = useMemo(() => {
        return isCuitValidForInvoice(effectiveClient?.documento_nro, effectiveClient?.condicion_iva);
    }, [effectiveClient]);
    
    // Get available invoice types based on CUIT validity, client tax condition, and AFIP org flag
    // ARCA regulations (RG 4291/2018, RG 4540/2019):
    // - Factura A: Para RI / Monotributo / Exento (con Solicitud de Factura A)
    // - Factura B: Para CF / Exento (sin solicitud) — IVA incluido
    // El Tercero Exento puede recibir A o B según la solicitud; mostramos ambas
    // opciones y la pre-validación al elegir A se hace en handleGenerateInvoice.
    const availableInvoiceTypes = useMemo(() => {
        const types = [];

        // Get the client's tax condition code (RI, M, CF, EX)
        const taxCode = effectiveClient?.condicion_iva || effectiveClient?.cliente_tipo || '';
        const condicionImpositiva = effectiveClient?.condicion_impositiva?.toUpperCase() || '';

        // Determine if client should receive Factura A (RI, M, o EX con CUIT válido)
        const isFacturaAClient =
            taxCode === 'RI' ||
            taxCode === 'M' ||
            (taxCode === 'EX' && cuitValid) ||
            condicionImpositiva.includes('RESPONSABLE INSCRIPTO') ||
            condicionImpositiva.includes('MONOTRIBUTO');

        // Determine if client should receive Factura B (CF o EX)
        const isFacturaBClient =
            taxCode === 'CF' ||
            taxCode === 'EX' ||
            condicionImpositiva.includes('CONSUMIDOR FINAL') ||
            condicionImpositiva.includes('EXENTO');

        // Factura A: Only for RI / M / EX (con CUIT) + AFIP enabled
        if (afipEnabled && cuitValid && INVOICE_FEATURES.FACTURA_A_ENABLED && isFacturaAClient) {
            types.push({ value: 'A', label: 'Factura A - Resp. Inscripto / Monotributo / Exento con solicitud' });
        }

        // Factura B: For CF / EX clients + AFIP enabled
        if (afipEnabled && INVOICE_FEATURES.FACTURA_B_ENABLED && isFacturaBClient) {
            types.push({ value: 'B', label: 'Factura B - Consumidor Final / Exento' });
        }

        // Factura X always available
        if (INVOICE_FEATURES.PRESUPUESTO_ENABLED) {
            types.push({ value: 'X', label: 'Factura X (Proforma)' });
        }

        // Fallback if nothing enabled
        if (types.length === 0) {
            types.push({ value: 'X', label: 'Factura X (Proforma)' });
        }

        return types;
    }, [afipEnabled, cuitValid, effectiveClient]);
    
    // Calculate totals based on selected price sources
    // NOTE: When using "Precio Original", we use subtotalFinal which is the POST-DISCOUNT amount
    // (the discount was already applied at CARGO time)
    const breakdown = useMemo(() => {
        if (!selectedItems || selectedItems.length === 0) {
            return {
                subtotalWithIVA: 0,
                subtotalWithoutIVA: 0,
                ivaAmount: 0,
                finalTotal: 0,
                itemsCount: 0,
                totalDiscountFromCargo: 0,
                hasCargoDiscount: false
            };
        }
        
        let subtotalWithIVA = 0;
        let totalDiscountFromCargo = 0;
        
        selectedItems.forEach(item => {
            const useActual = priceSourceMap[item.itemId] === 'actual';
            
            if (useActual) {
                // Precio Actual: apply the same movement-level discount as Original mode
                const discountMultiplier = 1 - ((item.descuentoPorcentaje ?? 0) / 100);
                const grossActual = item.precioActual * item.cantidad;
                subtotalWithIVA += grossActual * discountMultiplier;
                totalDiscountFromCargo += grossActual * (item.descuentoPorcentaje ?? 0) / 100;
            } else {
                // Precio Original: use subtotalFinal (POST-discount amount from CARGO time)
                // This is what the client actually owes
                subtotalWithIVA += item.subtotalFinal || item.subtotalCarga || (item.precioCarga * item.cantidad);
                totalDiscountFromCargo += item.descuentoMonto || 0;
            }
        });
        
        // Calculate IVA (assuming 21%)
        const divisor = 1.21;
        const subtotalWithoutIVA = subtotalWithIVA / divisor;
        const ivaAmount = subtotalWithIVA - subtotalWithoutIVA;
        
        return {
            subtotalWithIVA: Number(subtotalWithIVA.toFixed(2)),
            subtotalWithoutIVA: Number(subtotalWithoutIVA.toFixed(2)),
            ivaAmount: Number(ivaAmount.toFixed(2)),
            finalTotal: Number(subtotalWithIVA.toFixed(2)),
            itemsCount: selectedItems.length,
            totalDiscountFromCargo: Number(totalDiscountFromCargo.toFixed(2)),
            hasCargoDiscount: totalDiscountFromCargo > 0
        };
    }, [selectedItems, priceSourceMap]);
    
    // === EFFECTS ===
    
    // Reset ALL state when the modal opens — deps intentionally limited to [open]
    // so that tercero searches mid-session do NOT re-trigger this reset.
    useEffect(() => {
        if (open) {
            console.log('[FacturarCuentaAbierta] Modal opened with clientData:', clientData);

            setError('');
            setIsGenerating(false);
            setPaymentMode('single');
            setPayment1({ method: '201', amount: 0 });
            setPayment2({ method: '206', amount: 0 });
            setFacturarATercero(false);
            setTerceroData(null);
            setCuitInputTercero('');

            // Initialize price source map
            // If the movement was synced this session, default to 'actual' so the invoice
            // price matches the already-synced DEBE value. Otherwise default to 'original'.
            const initialPriceMap = {};
            selectedItems?.forEach(item => {
                // If the movement was synced (DEBE updated to current price), default to 'actual'.
                // No need to also check priceChanged — if wasMovementSynced=true, we always want actual.
                const defaultToActual = item.wasMovementSynced === true;
                initialPriceMap[item.itemId] = defaultToActual ? 'actual' : 'original';
            });
            setPriceSourceMap(initialPriceMap);
        }
    }, [open]); // ← only 'open' — do NOT add cuitValid/clientData here or tercero resets on search

    // Auto-select invoice type whenever the effective billing client changes (open OR tercero selected)
    // This is intentionally a SEPARATE effect so it never resets facturarATercero/terceroData.
    useEffect(() => {
        if (!open) return;

        const taxCode = effectiveClient?.condicion_iva || effectiveClient?.cliente_tipo || '';
        const condicionImpositiva = effectiveClient?.condicion_impositiva?.toUpperCase() || '';

        // Aplica para RI, M, y Exento con CUIT válido (éste último puede recibir
        // A bajo el régimen "Solicitud de Factura A" — la pre-validación se hace
        // al confirmar la emisión, en handleGenerateInvoice).
        const isFacturaAClient =
            taxCode === 'RI' ||
            taxCode === 'M' ||
            (taxCode === 'EX' && cuitValid) ||
            condicionImpositiva.includes('RESPONSABLE INSCRIPTO') ||
            condicionImpositiva.includes('MONOTRIBUTO');

        const isFacturaBClient =
            taxCode === 'CF' ||
            taxCode === 'EX' ||
            condicionImpositiva.includes('CONSUMIDOR FINAL') ||
            condicionImpositiva.includes('EXENTO');

        if (afipEnabled && cuitValid && isFacturaAClient && INVOICE_FEATURES.FACTURA_A_ENABLED) {
            setInvoiceType('A');
        } else if (afipEnabled && isFacturaBClient && INVOICE_FEATURES.FACTURA_B_ENABLED) {
            setInvoiceType('B');
        } else {
            setInvoiceType('X');
        }
    }, [open, afipEnabled, cuitValid, effectiveClient]);
    
    // === HANDLERS ===

    // Search tercero by CUIT — tries local DB first, then AFIP as fallback
    const handleSearchTercero = useCallback(async () => {
        const trimmed = cuitInputTercero.trim();
        if (!trimmed) return;
        try {
            setIsSearchingTercero(true);
            setError('');

            // Helper to normalize a raw client/AFIP result into a consistent shape
            const mapCondicionIva = (src) => {
                // Prefer explicit condicion_iva / cliente_tipo fields
                if (src.condicion_iva) return src.condicion_iva;
                if (src.cliente_tipo) return src.cliente_tipo;
                if (src.taxConditionCode) return src.taxConditionCode;
                // Derive from condicion_impositiva string
                const ci = (src.condicion_impositiva || '').toUpperCase();
                if (ci.includes('RESPONSABLE INSCRIPTO')) return 'RI';
                if (ci.includes('MONOTRIBUTO') || ci.includes('MONOTRIBUTISTA')) return 'M';
                if (ci.includes('EXENTO')) return 'EX';
                return 'CF'; // default
            };
            const normalize = (src, fallbackCuit) => ({
                razon_social: src.razon_social || src.businessName || src.nombre || fallbackCuit,
                documento_nro: src.documento_nro || src.cuit || fallbackCuit,
                documento_tipo: src.documento_tipo || 'CUIT',
                condicion_iva: mapCondicionIva(src),
                condicion_impositiva: src.condicion_impositiva || src.taxCondition || 'CONSUMIDOR FINAL',
                direccion: src.direccion || src.address || '',
            });

            // 1. Try local database first
            let resolved = null;
            try {
                const localResult = await searchClientByCuit(trimmed);
                if (localResult && localResult.razon_social) {
                    resolved = normalize(localResult, trimmed);
                }
            } catch (_) {
                // Not in local DB — that's fine, try AFIP next
            }

            // 2. Fallback: validate with AFIP
            if (!resolved) {
                try {
                    const afipResult = await validateCuitWithAfip(trimmed);
                    // validateCuitWithAfip can return { valid, data: {...} } or direct object
                    const afipData = afipResult?.data || afipResult;
                    if (afipData && afipData.razon_social) {
                        resolved = normalize(afipData, trimmed);
                    }
                } catch (_) {
                    // AFIP also failed — will show not-found error below
                }
            }

            if (resolved) {
                setTerceroData(resolved);
            } else {
                setError('No se encontró el CUIT en la base de datos ni en AFIP. Verifique el número e intente nuevamente.');
            }
        } catch (err) {
            setError('Error al buscar CUIT: ' + (err.message || 'Error desconocido'));
        } finally {
            setIsSearchingTercero(false);
        }
    }, [cuitInputTercero]);

    // Toggle Facturar a Tercero — when turning off, revert invoice type to client-based default
    const handleToggleTercero = useCallback((checked) => {
        setFacturarATercero(checked);
        if (!checked) {
            setTerceroData(null);
            setCuitInputTercero('');
        }
    }, []);

    // Set tercero as anonymous Consumidor Final (no CUIT) — enables Factura B without CUIT lookup
    const handleUseCFTercero = useCallback(() => {
        setTerceroData({
            razon_social: 'Consumidor Final',
            documento_nro: null,
            documento_tipo: 'DNI',
            condicion_iva: 'CF',
            condicion_impositiva: 'CONSUMIDOR FINAL',
            direccion: '',
        });
        setCuitInputTercero('');
    }, []);

    // Toggle price source for an item
    const handlePriceSourceChange = useCallback((itemId, source) => {
        setPriceSourceMap(prev => ({
            ...prev,
            [itemId]: source
        }));
    }, []);
    
    // Set all items to same price source
    const handleSetAllPriceSource = useCallback((source) => {
        const newMap = {};
        selectedItems?.forEach(item => {
            newMap[item.itemId] = source;
        });
        setPriceSourceMap(newMap);
    }, [selectedItems]);
    
    // Format currency
    const formatPrice = useCallback((price) => {
        if (typeof price !== 'number' || isNaN(price)) {
            return 'N/A';
        }
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);

    // Derived from selected items — used for Option A validation and both API call payloads.
    // batchIsPaid: true only if ALL selected CARGOs are already covered by client credit.
    const batchIsPaid = useMemo(
        () => (selectedItems?.length > 0 && selectedItems.every(item => item.isPaid === true)) ?? false,
        [selectedItems]
    );
    // hasMixedPaid: true when some but not all items are pre-paid — Option A blocks this.
    const hasMixedPaid = useMemo(
        () => !!(selectedItems?.some(item => item.isPaid === true) && selectedItems?.some(item => !item.isPaid)),
        [selectedItems]
    );

    // Handle invoice generation
    const handleGenerateInvoice = useCallback(async () => {
        // Validation
        if (!selectedItems || selectedItems.length === 0) {
            setError('No hay items seleccionados para facturar');
            return;
        }

        // Option A: block mixed isPaid batches — pre-paid and non-pre-paid CARGOs
        // cannot be invoiced together because they require different backend gates.
        if (hasMixedPaid) {
            setError(
                'No se pueden facturar juntos CARGOs pre-liquidados y no liquidados. ' +
                'Seleccione solo CARGOs del mismo tipo y vuelva a intentarlo.'
            );
            return;
        }

        // Validate split payment
        if (paymentMode === 'split') {
            const sum = payment1.amount + payment2.amount;
            const diff = Math.abs(sum - breakdown.finalTotal);
            
            if (diff > 0.01) {
                setError(`La suma de los pagos ($${sum.toFixed(2)}) debe ser igual al total ($${breakdown.finalTotal.toFixed(2)})`);
                return;
            }
            if (payment1.method === payment2.method) {
                setError('Los métodos de pago deben ser diferentes');
                return;
            }
            if (payment1.amount <= 0 || payment2.amount <= 0) {
                setError('Ambos montos de pago deben ser mayores a $0');
                return;
            }
        }
        
        try {
            setIsGenerating(true);
            setError('');
            
            // Determine client tax condition from actual client data
            // This should reflect the CLIENT'S tax status, not what the invoice type expects
            let clientTaxCondition = 'CF'; // Default to Consumidor Final

            // Use tercero data if Facturar a Tercero is active
            const billingClient = (facturarATercero && terceroData) ? terceroData : clientData;
            
            const condition = billingClient?.condicion_iva || billingClient?.cliente_tipo || '';
            const condicionImpositiva = billingClient?.condicion_impositiva || '';
            
            if (condition === 'RI' || condicionImpositiva.toUpperCase().includes('RESPONSABLE INSCRIPTO')) {
                clientTaxCondition = 'RI';
            } else if (condition === 'M' || condicionImpositiva.toUpperCase().includes('MONOTRIBUTO')) {
                clientTaxCondition = 'M';
            } else if (condition === 'EX' || condicionImpositiva.toUpperCase().includes('EXENTO')) {
                clientTaxCondition = 'EX';
            } else if (condition === 'CF' || condicionImpositiva.toUpperCase().includes('CONSUMIDOR FINAL')) {
                clientTaxCondition = 'CF';
            }
            
            console.log('[FacturarCuentaAbierta] Client tax condition resolved:', {
                condicion_iva: clientData?.condicion_iva,
                cliente_tipo: clientData?.cliente_tipo,
                condicion_impositiva: clientData?.condicion_impositiva,
                resolved: clientTaxCondition
            });
            
            // Prepare cart items from selected items
            // NOTE: RetailPrice is the PACK price (price for 'splitter' units)
            // Backend will calculate: unitPrice = RetailPrice / splitter
            //                         subtotal = quantity * unitPrice = quantity * (RetailPrice / splitter)
            // 
            // IMPORTANT: precioCarga is already the UNIT price (divided by splitter)
            // We need to convert it back to PACK price for the backend
            // Also, if discount was applied at CARGO time, we use subtotalFinal to derive the correct price
            const cartItems = selectedItems.map(item => {
                const useActual = priceSourceMap[item.itemId] === 'actual';
                const splitter = item.splitter || 1;
                
                let packPrice;
                if (useActual) {
                    // Using current price - apply same movement-level discount as Original mode
                    const discountMultiplier = 1 - ((item.descuentoPorcentaje ?? 0) / 100);
                    packPrice = item.precioActual * discountMultiplier * splitter;
                } else {
                    // Using original price from CARGO - need to account for discount already applied
                    // The subtotalFinal already has discount applied: subtotalFinal = subtotalCarga * (1 - discount%)
                    // We need: packPrice such that (packPrice / splitter) * quantity = subtotalFinal
                    // Therefore: packPrice = (subtotalFinal / quantity) * splitter
                    const discountPct = item.descuentoPorcentaje || 0;
                    if (discountPct > 0) {
                        // Discount was applied at CARGO - derive post-discount unit price
                        const postDiscountUnitPrice = item.precioCarga * (1 - discountPct / 100);
                        packPrice = postDiscountUnitPrice * splitter;
                        console.log('[FacturarCuentaAbierta] Discount applied at CARGO:', {
                            productCode: item.productCode,
                            originalUnitPrice: item.precioCarga,
                            discountPct,
                            postDiscountUnitPrice,
                            packPrice
                        });
                    } else {
                        // No discount - just convert unit price to pack price
                        packPrice = item.precioCarga * splitter;
                    }
                }
                
                return {
                    ProductId: item.productId,
                    ProductCode: item.productCode,
                    ProductDescription: item.productName,
                    quantity: item.cantidad,
                    splitter: splitter,
                    RetailPrice: packPrice, // Pack price - backend divides by splitter
                    forceRetailPrice: true, // ✅ Force V2 API to use this price (historical from CARGO)
                    // ✅ Set isVolumePricingActive to tell backend to USE the provided RetailPrice
                    // instead of looking it up from database. This is needed for historical pricing.
                    isVolumePricingActive: true
                };
            });
            
            // Prepare split payment data
            const effectivePaymentMethod = paymentMode === 'split' ? '208' : paymentMethod;
            const splitPaymentData = paymentMode === 'split' ? {
                payment1: {
                    method: payment1.method,
                    name: getPaymentMethodName(payment1.method),
                    amount: payment1.amount
                },
                payment2: {
                    method: payment2.method,
                    name: getPaymentMethodName(payment2.method),
                    amount: payment2.amount
                }
            } : null;
            
            // Build invoice payload
            // requiereFacturaA: true cuando el operador está emitiendo Factura A
            // a un Tercero Exento. El backend usa este flag para permitir el
            // camino RI→EX→A (válido bajo Solicitud de Factura A del receptor).
            const requiereFacturaA =
                !!facturarATercero &&
                clientTaxCondition === 'EX' &&
                invoiceType === 'A';

            const invoicePayload = {
                cartItems,
                discountPercentage: 0, // Discount already applied at CARGO time
                invoiceType: invoiceType,
                paymentMethod: effectivePaymentMethod,
                clientData: {
                    documento_tipo: billingClient.documento_tipo || 'CUIT', // ✅ Required for AFIP
                    documento_nro: billingClient.documento_nro,
                    razon_social: billingClient.razon_social,
                    direccion: billingClient.direccion || '',
                    condicion_impositiva: billingClient.condicion_impositiva || 'CONSUMIDOR FINAL',
                    cliente_tipo: billingClient.condicion_iva || 'CF',
                    condicion_iva: billingClient.condicion_iva || 'CF'
                },
                // Track that this was invoiced to a third party for RA record
                runningAccountClientId: clientData?.id || clientData?.documento_nro,
                facturadoATercero: facturarATercero && !!terceroData,
                clientTaxCondition: clientTaxCondition,
                requiereFacturaA: requiereFacturaA, // Tercero EX solicita A
                registerWithAFIP: invoiceType !== 'X', // X doesn't register with AFIP
                isBudgetQuote: false,
                paymentMode: paymentMode,
                splitPayment: splitPaymentData,
                // Identifies the origin of this invoice request — useful for backend logging/routing
                invoiceSource: 'running-account',
                isPaid: batchIsPaid          // RA pre-paid gate: activates analytics exclusion + Caja bypass
            };

            // Pre-validación: si vamos a emitir Factura A a un Tercero Exento,
            // bloqueamos preventivamente si la Solicitud de Factura A no está
            // activa en AFIP para ese CUIT. Solo aplica al camino "Facturar a
            // Tercero" — no se ejecuta en Facturar normal.
            if (requiereFacturaA) {
                const check = await checkTerceroExentoSolicitaA(
                    billingClient.documento_nro,
                    clientTaxCondition
                );
                if (!check.permite) {
                    setError(check.mensaje);
                    setIsGenerating(false);
                    return;
                }
            }

            console.log('📋 [FacturarCuentaAbierta] Invoice payload:', invoicePayload);
            
            // Generate invoice using same service as InvoicePreviewModal
            const result = await createInvoiceV2(invoicePayload, {});
            
            console.log('✅ [FacturarCuentaAbierta] Invoice result:', result);
            
            if (result.success && result.invoice) {
                // Mark running account items as invoiced
                // Extract item IDs from selected items
                const itemIds = selectedItems.map(item => item.itemId);
                
                console.log('🔗 [FacturarCuentaAbierta] Invoicing RA items — Call 2:', {
                    clientId: clientData.id,
                    invoiceId: result.invoice.invoice_id,
                    invoiceNumber: result.invoice.numero_comprobante || result.invoice.comprobante_nro,
                    isPaid: batchIsPaid,
                    paymentMethod: getPaymentMethodName(effectivePaymentMethod),
                    itemCount: itemIds.length
                });
                
                // Call 2: invoice/link items via the Caja-gated endpoint
                const linkResult = await runningAccountService.invoiceRunningAccount({
                    clientId: clientData.id,
                    invoiceId: result.invoice.invoice_id,
                    invoiceNumber: result.invoice.numero_comprobante || result.invoice.comprobante_nro,
                    paymentMethod: getPaymentMethodName(effectivePaymentMethod),
                    isPaid: batchIsPaid,
                    selectedItems: selectedItems.map(item => ({
                        itemId: item.itemId,
                        invoiceItemId: null,  // not yet available from Call 1 response
                        usePrecioActual: priceSourceMap[item.itemId] === 'actual',
                        precioActual: item.precioActual,
                        precioCarga: item.precioCarga,
                        cantidad: item.cantidad
                    }))
                });
                
                if (linkResult.success) {
                    console.log('✅ [FacturarCuentaAbierta] Items linked to invoice:', linkResult);
                } else {
                    console.warn('⚠️ [FacturarCuentaAbierta] Failed to link items to invoice:', linkResult.message);
                    // Don't fail the whole operation - invoice was created successfully
                }
                
                // Prepare invoice data for success callback
                const invoiceData = {
                    invoiceId: result.invoice.invoice_id,
                    invoiceNumber: result.invoice.numero_comprobante || result.invoice.comprobante_nro,
                    invoiceType: invoiceType,
                    cae: result.invoice.cae,
                    caeVencimiento: result.invoice.vencimiento_cae,
                    total: breakdown.finalTotal,
                    pdfUrl: result.invoice.comprobante_pdf_url,
                    qrUrl: result.invoice.afip_qr,
                    clientData: clientData,
                    selectedItems: selectedItems.map(item => ({
                        ...item,
                        usedPrecioActual: priceSourceMap[item.itemId] === 'actual'
                    }))
                };
                
                // Notify parent
                if (onInvoiceGenerated) {
                    onInvoiceGenerated(invoiceData);
                }
                
                // Clear selection
                if (onClearSelection) {
                    onClearSelection();
                }
                
                // Close modal
                onClose();
            } else {
                throw new Error(result.message || 'Error desconocido al generar factura');
            }
            
        } catch (err) {
            console.error('❌ [FacturarCuentaAbierta] Error:', err);
            setError(`Error al generar factura: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsGenerating(false);
        }
    }, [selectedItems, priceSourceMap, invoiceType, paymentMethod, paymentMode, payment1, payment2, breakdown, clientData, batchIsPaid, hasMixedPaid, onInvoiceGenerated, onClearSelection, onClose]);
    
    // === RENDER ===
    
    if (!clientData) {
        return null;
    }
    
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '95vh',
                    minHeight: '600px',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle sx={{ 
                backgroundColor: '#47ba8c', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon fontSize="large" />
                    <Typography variant="h5" component="div">
                        Facturar desde Cuenta Abierta
                    </Typography>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    aria-label="close"
                    disabled={isGenerating}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Client Information */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" color="primary">
                            {facturarATercero ? 'Facturar a Tercero' : 'Datos del Cliente (Cuenta Abierta)'}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={facturarATercero}
                                    onChange={(e) => handleToggleTercero(e.target.checked)}
                                    disabled={isGenerating}
                                    color="warning"
                                />
                            }
                            label={<Typography variant="body2" fontWeight="bold" color={facturarATercero ? 'warning.main' : 'text.secondary'}>Facturar a Tercero</Typography>}
                            labelPlacement="start"
                        />
                    </Box>

                    {/* Original RA Client — always shown as reference */}
                    <Box sx={{ p: 1.5, backgroundColor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50', mb: facturarATercero ? 1.5 : 0 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, textTransform: 'uppercase', fontWeight: 'bold' }}>Cliente de Cuenta Abierta</Typography>
                        <Typography variant="body2" fontWeight="bold">{clientData.razon_social}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {clientData.documento_nro ? `CUIT: ${clientData.documento_nro}` : 'Sin CUIT'} · {clientData.condicion_impositiva || 'Consumidor Final'}
                        </Typography>
                        {facturarATercero && (
                            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                                ⚠️ Los cargos quedan vinculados a esta cuenta — solo la factura se emite al tercero.
                            </Typography>
                        )}
                    </Box>

                    {/* Tercero Section */}
                    {facturarATercero && (
                        <Box sx={{ p: 2, backgroundColor: '#fff8e1', borderRadius: 1, border: '2px solid #ffc107' }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="warning.dark" sx={{ mb: 1.5 }}>
                                Datos de facturación (Tercero)
                            </Typography>

                            {!terceroData ? (
                                <>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="CUIT del tercero"
                                            placeholder="20-12345678-9"
                                            value={cuitInputTercero}
                                            onChange={(e) => setCuitInputTercero(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && !isSearchingTercero) handleSearchTercero(); }}
                                            disabled={isSearchingTercero || isGenerating}
                                            helperText="Ingrese el CUIT (11 dígitos) del tercero"
                                            inputProps={{ maxLength: 20 }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            size="small"
                                            startIcon={isSearchingTercero ? <CircularProgress size={16} color="inherit" /> : <PersonSearchIcon />}
                                            onClick={handleSearchTercero}
                                            disabled={isSearchingTercero || isGenerating || !cuitInputTercero.trim()}
                                            sx={{ minWidth: 110, height: 40, mt: 0.2 }}
                                        >
                                            {isSearchingTercero ? 'Buscando...' : 'Buscar'}
                                        </Button>
                                    </Box>
                                    <Divider sx={{ my: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary">O</Typography>
                                    </Divider>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="warning"
                                        size="small"
                                        onClick={handleUseCFTercero}
                                        disabled={isSearchingTercero || isGenerating}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Consumidor Final (sin CUIT) — Factura B
                                    </Button>
                                </>
                            ) : (
                                <Box>
                                    <Box sx={{ p: 1.5, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107', mb: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">{terceroData.razon_social}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    CUIT: {terceroData.documento_nro || '—'} · {terceroData.condicion_impositiva || 'Consumidor Final'}
                                                </Typography>
                                                {terceroData.direccion && (
                                                    <Typography variant="body2" color="text.secondary">Dirección: {terceroData.direccion}</Typography>
                                                )}
                                                <Box sx={{ mt: 0.5 }}>
                                                    {cuitValid ? (
                                                        <Chip label="✅ CUIT válido - Factura A/B/X" color="success" size="small" />
                                                    ) : (
                                                        <Chip label="⚠️ Sin CUIT válido - Factura B/X" color="warning" size="small" />
                                                    )}
                                                </Box>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => { setTerceroData(null); setCuitInputTercero(''); }}
                                                disabled={isGenerating}
                                            >
                                                Cambiar
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* CUIT validity indicator when NOT using tercero */}
                    {!facturarATercero && (
                        <Box sx={{ mt: 1 }}>
                            {cuitValid ? (
                                <Chip label="✅ CUIT válido - Factura A/B/X disponible" color="success" size="small" />
                            ) : (
                                <Chip
                                    label={
                                        (clientData?.condicion_iva === 'CF' || clientData?.condicion_iva === 'EX' ||
                                         clientData?.condicion_impositiva?.toUpperCase().includes('CONSUMIDOR FINAL') ||
                                         clientData?.condicion_impositiva?.toUpperCase().includes('EXENTO'))
                                            ? '⚠️ Sin CUIT - Factura B/X disponible'
                                            : '⚠️ Sin CUIT válido - Solo Factura X disponible'
                                    }
                                    color="warning"
                                    size="small"
                                />
                            )}
                        </Box>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Invoice Configuration */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Configuración de Comprobante
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2
                    }}>
                        {/* Invoice Type Selector */}
                        <FormControl fullWidth>
                            <InputLabel id="invoice-type-label">Tipo de Comprobante</InputLabel>
                            <Select
                                labelId="invoice-type-label"
                                id="invoice-type"
                                value={invoiceType}
                                label="Tipo de Comprobante"
                                onChange={(e) => setInvoiceType(e.target.value)}
                                disabled={isGenerating}
                            >
                                {availableInvoiceTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Payment Method Selector - Single Mode Only */}
                        {paymentMode === 'single' && (
                            <FormControl fullWidth>
                                <InputLabel id="payment-method-label">Método de Pago</InputLabel>
                                <Select
                                    labelId="payment-method-label"
                                    id="payment-method"
                                    value={paymentMethod}
                                    label="Método de Pago"
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                    disabled={isGenerating}
                                >
                                    <MenuItem value="201">Contado</MenuItem>
                                    <MenuItem value="202">Tarjeta de débito</MenuItem>
                                    <MenuItem value="203">Tarjeta de crédito</MenuItem>
                                    <MenuItem value="204">Cuenta corriente</MenuItem>
                                    <MenuItem value="205">Cheque</MenuItem>
                                    <MenuItem value="206">Transferencia Bancaria</MenuItem>
                                    <MenuItem value="207">Otros medios de pago electrónico</MenuItem>
                                    <MenuItem value="208">Otra</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    </Box>

                    {/* Payment Mode Selection */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Configuración de Pago
                        </Typography>
                        
                        <FormControl component="fieldset">
                            <RadioGroup
                                row
                                value={paymentMode}
                                onChange={(e) => {
                                    setPaymentMode(e.target.value);
                                    if (e.target.value === 'split') {
                                        setPayment1({ method: '201', amount: 0 });
                                        setPayment2({ method: '206', amount: 0 });
                                    }
                                }}
                            >
                                <FormControlLabel
                                    value="single"
                                    control={<Radio />}
                                    label="Un método de pago"
                                    disabled={isGenerating}
                                />
                                <FormControlLabel
                                    value="split"
                                    control={<Radio />}
                                    label="Dos métodos de pago (dividir)"
                                    disabled={isGenerating}
                                />
                            </RadioGroup>
                        </FormControl>

                        {/* Split Payment Selector */}
                        {paymentMode === 'split' && (
                            <Box sx={{ mt: 2 }}>
                                <SplitPaymentSelector
                                    total={breakdown.finalTotal}
                                    payment1={payment1}
                                    payment2={payment2}
                                    onPayment1Change={setPayment1}
                                    onPayment2Change={setPayment2}
                                    disabled={isGenerating}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Items to Invoice */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" color="primary">
                            Items a Facturar ({breakdown.itemsCount})
                        </Typography>
                        
                        {/* Bulk price source selector */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => handleSetAllPriceSource('original')}
                                disabled={isGenerating}
                            >
                                Usar Precio Original
                            </Button>
                            <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => handleSetAllPriceSource('actual')}
                                disabled={isGenerating}
                            >
                                Usar Precio Actual
                            </Button>
                        </Box>
                    </Box>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Precio Original:</strong> El precio al momento del CARGO. 
                            <strong> Precio Actual:</strong> El precio actual del producto (puede haber cambiado).
                        </Typography>
                    </Alert>
                    
                    <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Código</strong></TableCell>
                                    <TableCell><strong>Descripción</strong></TableCell>
                                    <TableCell align="center"><strong>Cant.</strong></TableCell>
                                    <TableCell align="center"><strong>Stock</strong></TableCell>
                                    <TableCell align="right"><strong>P. Original</strong></TableCell>
                                    <TableCell align="right"><strong>P. Actual</strong></TableCell>
                                    <TableCell align="center"><strong>Usar</strong></TableCell>
                                    <TableCell align="right"><strong>Subtotal</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedItems?.map((item) => {
                                    const useActual = priceSourceMap[item.itemId] === 'actual';
                                    const selectedPrice = useActual ? item.precioActual : item.precioCarga;
                                    // Both modes show post-discount subtotal for consistency with what's invoiced
                                    const discountMultiplier = 1 - ((item.descuentoPorcentaje ?? 0) / 100);
                                    const subtotal = useActual 
                                        ? (item.precioActual * item.cantidad * discountMultiplier)
                                        : (item.subtotalFinal || item.subtotalCarga || (item.precioCarga * item.cantidad));
                                    // Prefer backend-computed priceChanged (pack-level comparison, no false positives for pack products)
                                    const priceChanged = item.priceChanged ?? (Math.abs(item.precioCarga - item.precioActual) > 0.01);
                                    const hasDiscount = item.descuentoPorcentaje > 0; // Always show discount tag — it's applied in both Original and Actual modes
                                    
                                    return (
                                        <TableRow key={item.itemId} hover>
                                            <TableCell>{item.productCode}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                    {item.productName}
                                                </Typography>
                                                {hasDiscount && (
                                                    <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                                        -{item.descuentoPorcentaje}% aplicado al cargar
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">{item.cantidad}</TableCell>
                                            <TableCell align="center">
                                                {item.affectsStock || item.affectsStock === undefined ? (
                                                    <Tooltip title="Stock descontado al cargar">
                                                        <CheckCircleIcon 
                                                            fontSize="small" 
                                                            sx={{ color: 'success.main', verticalAlign: 'middle' }}
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Stock NO descontado (se descontará al facturar)">
                                                        <MinusCircleIcon 
                                                            fontSize="small" 
                                                            sx={{ color: 'warning.main', verticalAlign: 'middle' }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        fontWeight: !useActual ? 'bold' : 'normal',
                                                        color: !useActual ? 'success.main' : 'text.secondary'
                                                    }}
                                                >
                                                    {formatPrice(item.precioCarga)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            fontWeight: useActual ? 'bold' : 'normal',
                                                            color: useActual ? 'success.main' : 'text.secondary'
                                                        }}
                                                    >
                                                        {formatPrice(item.precioActual)}
                                                    </Typography>
                                                    {priceChanged && (
                                                        <Tooltip title="El precio ha cambiado desde el CARGO">
                                                            <WarningIcon 
                                                                fontSize="small" 
                                                                color="warning" 
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Select
                                                    size="small"
                                                    value={priceSourceMap[item.itemId] || 'original'}
                                                    onChange={(e) => handlePriceSourceChange(item.itemId, e.target.value)}
                                                    disabled={isGenerating}
                                                    sx={{ minWidth: 100 }}
                                                >
                                                    <MenuItem value="original">Original</MenuItem>
                                                    <MenuItem value="actual">Actual</MenuItem>
                                                </Select>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight="bold">
                                                    {formatPrice(subtotal)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Financial Summary */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Resumen Financiero
                    </Typography>
                    
                    {/* Show CARGO discount notice if applicable */}
                    {breakdown.hasCargoDiscount && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Descuento incluido:</strong> El total ya incluye el descuento de{' '}
                                <strong>{formatPrice(breakdown.totalDiscountFromCargo)}</strong> aplicado al momento del CARGO.
                            </Typography>
                        </Alert>
                    )}
                    
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 2,
                        p: 3,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1">
                                Subtotal (Sin IVA):
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatPrice(breakdown.subtotalWithoutIVA)}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1">
                                IVA (21%):
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatPrice(breakdown.ivaAmount)}
                            </Typography>
                        </Box>

                        <Divider />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h5" color="success.main">
                                Total a Facturar:
                            </Typography>
                            <Typography variant="h4" fontWeight="bold" color="success.main">
                                {formatPrice(breakdown.finalTotal)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Informational Alerts */}
                {invoiceType === 'X' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Factura X (Proforma):</strong> Este comprobante NO se registra en AFIP 
                            y NO genera CAE. Es un comprobante interno para el cliente.
                        </Typography>
                    </Alert>
                )}
                
                {invoiceType === 'A' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Factura A (IVA Discriminado):</strong> Se generará con CAE de AFIP. 
                            El IVA se muestra discriminado en el comprobante.
                        </Typography>
                    </Alert>
                )}
                
                {invoiceType === 'B' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Factura B (Consumidor Final):</strong> Se generará con CAE de AFIP. 
                            El IVA está incluido en el precio final. {!cuitValid && 'Puede emitirse sin CUIT para CONSUMIDOR FINAL.'}
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    width: '100%',
                    justifyContent: 'space-between'
                }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isGenerating}
                        sx={{ flex: 1 }}
                    >
                        Cancelar
                    </Button>
                    
                    <Button
                        variant="contained"
                        startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        onClick={handleGenerateInvoice}
                        disabled={
                            isGenerating || 
                            !selectedItems || 
                            selectedItems.length === 0 ||
                            (paymentMode === 'split' && (
                                Math.abs((payment1.amount + payment2.amount) - breakdown.finalTotal) > 0.01 ||
                                payment1.method === payment2.method ||
                                payment1.amount <= 0 ||
                                payment2.amount <= 0
                            ))
                        }
                        sx={{ 
                            flex: 1,
                            backgroundColor: '#47ba8c',
                            '&:hover': { backgroundColor: '#3a9572' }
                        }}
                    >
                        {isGenerating 
                            ? (invoiceType === 'X' ? 'Generando...' : 'Enviando a AFIP...')
                            : (invoiceType === 'X' ? '📋 Generar Factura X' : 'Confirmar y Enviar a AFIP')
                        }
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

FacturarCuentaAbiertaModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    clientData: PropTypes.shape({
        id: PropTypes.number,
        documento_nro: PropTypes.string,
        razon_social: PropTypes.string.isRequired,
        condicion_impositiva: PropTypes.string,
        condicion_iva: PropTypes.string,
        direccion: PropTypes.string
    }),
    selectedItems: PropTypes.arrayOf(PropTypes.shape({
        itemId: PropTypes.number.isRequired,
        movementId: PropTypes.number.isRequired,
        productId: PropTypes.number,
        productCode: PropTypes.string.isRequired,
        productName: PropTypes.string.isRequired,
        cantidad: PropTypes.number.isRequired,
        precioCarga: PropTypes.number.isRequired,
        precioActual: PropTypes.number.isRequired
    })).isRequired,
    onInvoiceGenerated: PropTypes.func.isRequired,
    onClearSelection: PropTypes.func
};

export default FacturarCuentaAbiertaModal;
